import React, { useEffect, useState } from 'react';
import { MsalProvider, AuthenticatedTemplate, UnauthenticatedTemplate, useMsal } from '@azure/msal-react';
import { FluentProvider, webLightTheme, Spinner, makeStyles } from '@fluentui/react-components';
import { BrowserRouter as Router } from 'react-router-dom';
import { AppInsightsContext } from '@microsoft/applicationinsights-react-js';
import { NavigationBar } from './components/NavigationBar';
import { AppLayout } from './components/AppLayout';
import { NoAccessProvider } from './context';
import { getLoginRequest, isTestMode } from './config';
import { PublicClientApplication } from '@azure/msal-browser';
import { appInsights, reactPlugin } from './telemetry/appInsights';

import './styles/App.css';

const useStyles = makeStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '70vh',
        textAlign: 'center',
        gap: '16px',
    },
});

interface AppProps {
    msalInstance: PublicClientApplication;
}

/**
 * Main content component that handles authentication flow.
 * This component requires the user to be authenticated via corporate Azure AD.
 */
const MainContent: React.FC = () => {
    const { instance, inProgress, accounts } = useMsal();
    const styles = useStyles();
    const [accountWithClaims, setAccountWithClaims] = useState<any>(null);

    // Get the login request for corporate authentication
    const currentLoginRequest = React.useMemo(() => getLoginRequest(), []);

    // Get the first account
    const account = accounts[0];

    // Effect to acquire token claims if needed
    useEffect(() => {
        // Don't do anything if MSAL is processing
        if (inProgress !== 'none') {
            return;
        }

        // If we already have claims, nothing to do
        if (accountWithClaims) {
            return;
        }

        // Check if account already has claims from the ID token
        if (account) {
            const activeAccount = instance.getActiveAccount();
            if (activeAccount?.idTokenClaims) {
                console.log('Using active account with claims');
                setAccountWithClaims(activeAccount);
                // Set App Insights authenticated user context
                const userId = (activeAccount.idTokenClaims as any).preferred_username ||
                    (activeAccount.idTokenClaims as any).email ||
                    activeAccount.username || '';
                if (userId) {
                    appInsights.setAuthenticatedUserContext(userId, undefined, true);
                }
                return;
            }
            if (account?.idTokenClaims) {
                console.log('Using account with claims');
                setAccountWithClaims(account);
                // Set App Insights authenticated user context
                const userId = (account.idTokenClaims as any).preferred_username ||
                    (account.idTokenClaims as any).email ||
                    account.username || '';
                if (userId) {
                    appInsights.setAuthenticatedUserContext(userId, undefined, true);
                }
                return;
            }

            // Need to acquire token silently
            console.log('Acquiring token silently for corporate...');
            instance.acquireTokenSilent({
                scopes: currentLoginRequest.scopes,
                account: account,
                forceRefresh: false
            }).then((response) => {
                console.log('Token acquired, setting account with claims');
                setAccountWithClaims(response.account);
            }).catch((error) => {
                console.error('Silent token acquisition failed:', error);
                appInsights.trackException({ exception: error });
                // Just use the account as-is for errors
                setAccountWithClaims(account);
            });
        }
    }, [account, accountWithClaims, inProgress, instance, currentLoginRequest]);

    // Show loading while MSAL is processing or we don't have account with claims yet
    if (inProgress !== 'none' || (account && !accountWithClaims)) {
        return (
            <div className={styles.container}>
                <Spinner size="large" label="Loading..." />
            </div>
        );
    }

    return (
        <>
            <NavigationBar />
            <main id="maincontent">
                <AppLayout />
            </main>
        </>
    );
};

/**
 * Test mode content - bypasses authentication for accessibility testing
 */
const TestModeContent: React.FC = () => {
    return (
        <>
            <NavigationBar />
            <main id="maincontent">
                <AppLayout />
            </main>
        </>
    );
};

/**
 * Login component - triggers MSAL login redirect when not authenticated
 */
const LoginRedirect: React.FC = () => {
    const { instance, inProgress } = useMsal();
    const styles = useStyles();
    const [isRedirecting, setIsRedirecting] = useState(false);

    const currentLoginRequest = React.useMemo(() => getLoginRequest(), []);

    useEffect(() => {
        // Don't do anything if already redirecting or MSAL is processing
        if (isRedirecting || inProgress !== 'none') {
            return;
        }

        console.log('Not authenticated, redirecting to corporate login...');
        setIsRedirecting(true);
        
        // Clear any cached MSAL interaction state
        const keysToRemove = Object.keys(sessionStorage).filter(key => 
            key.startsWith('msal.') && key.includes('interaction')
        );
        keysToRemove.forEach(key => sessionStorage.removeItem(key));
        
        instance.loginRedirect(currentLoginRequest).catch((error) => {
            console.log('Login redirect error:', error);
            appInsights.trackException({ exception: error });
            setIsRedirecting(false);
        });
    }, [instance, inProgress, currentLoginRequest, isRedirecting]);

    return (
        <div className={styles.container}>
            <Spinner size="large" label="Redirecting to sign in..." />
        </div>
    );
};

/**
 * Main App component
 */
const App: React.FC<AppProps> = ({ msalInstance }) => {
    const handleNoAccess = (message: string) => {
        console.warn(message);
    };

    // If in test mode, bypass authentication
    if (isTestMode) {
        return (
            <FluentProvider theme={webLightTheme}>
                <Router>
                    <NoAccessProvider onNoAccess={handleNoAccess}>
                        <a href="#maincontent" className="sr-only">Skip to main content</a>
                        <TestModeContent />
                    </NoAccessProvider>
                </Router>
            </FluentProvider>
        );
    }

    return (
        <MsalProvider instance={msalInstance}>
            <AppInsightsContext.Provider value={reactPlugin}>
                <FluentProvider theme={webLightTheme}>
                    <Router>
                        <NoAccessProvider onNoAccess={handleNoAccess}>
                            <a href="#maincontent" className="sr-only">Skip to main content</a>
                            <AuthenticatedTemplate>
                                <MainContent />
                            </AuthenticatedTemplate>
                            <UnauthenticatedTemplate>
                                <LoginRedirect />
                            </UnauthenticatedTemplate>
                        </NoAccessProvider>
                    </Router>
                </FluentProvider>
            </AppInsightsContext.Provider>
        </MsalProvider>
    );
};

export default App;
